<script setup lang="ts">
import {
  PhChatCircleDots,
  PhCompass,
  PhList,
  PhMagnifyingGlass,
  PhPlusSquare,
  PhUserCircle,
  PhUserPlus,
} from '@phosphor-icons/vue'
import { storeToRefs } from 'pinia'
import { computed, type Component } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppBrandLogo from '@/components/app-brand-logo'
import AppSidebarMoreMenu from '@/components/app-sidebar-more-menu'
import { requestRouteRefresh } from '@/composables/useRouteRefreshSignal'
import { useToast } from '@/composables/useToast'
import {
  APP_BUILDING_MESSAGE,
  APP_NAV_MORE_LABEL,
  APP_ROUTE_DEFINITIONS,
  type AppNavIconKey,
} from '@/constants/routes'
import { useInitUser, syncUserFromCookie } from '@/composables/useInitUser'
import { useUserStore } from '@/stores/user'
import { optimizeAvatarUrl } from '@/utils/distribute/media-url'
import type { Props } from './type'

defineOptions({
  name: 'AppSidebar',
})

defineProps<Props>()

interface NavItem {
  key: string
  label: string
  path: string
  icon: Component
  available: boolean
  requiresAuth: boolean
  externalUrl?: string
}

const NAV_ICON_MAP: Record<AppNavIconKey, Component> = {
  discover: PhCompass,
  follow: PhUserPlus,
  messages: PhChatCircleDots,
  search: PhMagnifyingGlass,
  create: PhPlusSquare,
  me: PhUserCircle,
}

const route = useRoute()
const router = useRouter()
const toast = useToast()
const userStore = useUserStore()
const { userInfo } = storeToRefs(userStore)

const navItems: NavItem[] = APP_ROUTE_DEFINITIONS.map((item) => ({
  key: item.key,
  label: item.label,
  path: item.path,
  icon: NAV_ICON_MAP[item.iconKey],
  available: item.available,
  requiresAuth: item.requiresAuth === true,
  externalUrl: item.externalUrl,
}))
const discoverNavItem = navItems.find((item) => item.key === 'discover') ?? null

const activePath = computed(() => route.path)
const userAvatarSrc = computed(() => {
  if (!userInfo.value?.token) {
    return ''
  }

  return optimizeAvatarUrl(userInfo.value.avatar) ?? userInfo.value.avatar ?? ''
})

async function go(item: NavItem) {
  if (item.externalUrl) {
    window.open(item.externalUrl, '_blank', 'noopener,noreferrer')
    return
  }

  if (item.requiresAuth && !syncUserFromCookie(userStore)) {
    await useInitUser()

    if (!userStore.isLoggedIn()) {
      return
    }
  }

  if (activePath.value === item.path) {
    requestRouteRefresh(item.key)
    window.scrollTo({ top: 0, behavior: 'auto' })
    return
  }

  if (!item.available) {
    toast.info(APP_BUILDING_MESSAGE)
  }

  await router.push(item.path)
}

async function handleBrandClick() {
  if (!discoverNavItem) {
    return
  }

  await go(discoverNavItem)
}
</script>

<template>
  <aside class="app-sidebar">
    <button
      type="button"
      class="app-sidebar__brand app-sidebar__brand-button"
      aria-label="返回发现首页"
      @click="handleBrandClick"
    >
      <AppBrandLogo class="app-sidebar__logo" />
    </button>

    <nav class="app-sidebar__nav">
      <button
        v-for="item in navItems"
        :key="item.key"
        type="button"
        class="app-sidebar__item"
        :class="{ 'is-active': activePath === item.path }"
        @click="go(item)"
      >
        <span class="app-sidebar__icon">
          <span
            v-if="item.key === 'me' && userAvatarSrc"
            class="app-sidebar__avatar"
            aria-hidden="true"
          >
            <img :src="userAvatarSrc" alt="" />
          </span>
          <component
            v-else
            :is="item.icon"
            :size="24"
            :weight="activePath === item.path ? 'fill' : 'regular'"
          />
        </span>
        <span class="app-sidebar__label">{{ item.label }}</span>
      </button>
    </nav>

    <div class="app-sidebar__more">
      <AppSidebarMoreMenu>
        <span class="app-sidebar__item">
          <span class="app-sidebar__icon">
            <PhList :size="24" />
          </span>
          <span class="app-sidebar__label">{{ APP_NAV_MORE_LABEL }}</span>
        </span>
      </AppSidebarMoreMenu>
    </div>
  </aside>
</template>

<style scoped>
.app-sidebar {
  position: sticky;
  top: 0;
  align-self: start;
  display: flex;
  box-sizing: border-box;
  width: 155px;
  min-height: 100vh;
  flex-direction: column;
  gap: 0;
  padding: 12px 8px;
  background: var(--profile-bg, #fff);
}

.app-sidebar__brand {
  display: flex;
  box-sizing: border-box;
  width: 139px;
  min-height: 48px;
  align-items: center;
  padding: 8px 12px;
}

.app-sidebar__brand-button {
  cursor: pointer;
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
}

.app-sidebar__brand-button:focus-visible {
  outline: 2px solid var(--profile-text, rgba(0, 0, 0, 0.96));
  outline-offset: 2px;
}

.app-sidebar__logo {
  flex: 0 0 auto;
}

.app-sidebar__nav {
  display: flex;
  width: 139px;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 0;
}

.app-sidebar__item {
  display: flex;
  box-sizing: border-box;
  width: 139px;
  min-height: 48px;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  color: var(--profile-muted-strong, rgba(0, 0, 0, 0.7));
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  cursor: pointer;
  border-radius: 40px;
  transition:
    background-color var(--app-interaction-duration) var(--app-interaction-ease),
    color var(--app-interaction-duration) var(--app-interaction-ease);
}

.app-sidebar__icon {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  place-items: center;
}

.app-sidebar__avatar {
  display: block;
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  padding: 1px;
  border: 2px solid transparent;
  border-radius: 50%;
  background: var(--app-avatar-surface);
  box-shadow: inset 0 0 0 1px var(--app-avatar-ring);
}

.app-sidebar__avatar img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.app-sidebar__item.is-active .app-sidebar__avatar {
  border-color: var(--profile-text, rgba(0, 0, 0, 0.96));
}

.app-sidebar__label {
  min-width: 0;
}

.app-sidebar__item.is-active {
  color: var(--profile-text, rgba(0, 0, 0, 0.96));
}

.app-sidebar__item:hover,
.app-sidebar__item:focus-visible {
  background-color: var(--app-state-layer-hover);
  color: var(--profile-text, rgba(0, 0, 0, 0.96));
}

.app-sidebar__item:active {
  background-color: var(--app-state-layer-pressed);
}

.app-sidebar__more {
  width: 139px;
  margin-top: auto;
}

@media (max-width: 1024px) and (min-width: 961px) {
  .app-sidebar {
    width: 76px;
    align-items: flex-start;
    padding: 12px 8px;
  }

  .app-sidebar__brand {
    width: 56px;
    min-height: 48px;
    justify-content: center;
    padding: 8px 12px;
  }

  .app-sidebar__nav {
    width: 56px;
    align-items: flex-start;
  }

  .app-sidebar__item {
    width: 56px;
    min-height: 48px;
    justify-content: center;
    gap: 0;
    padding: 8px 12px;
  }

  .app-sidebar__more {
    display: flex;
    width: 56px;
    justify-content: center;
  }

  .app-sidebar__label {
    display: none;
  }
}

@media (max-width: 960px) {
  .app-sidebar {
    position: static;
    width: auto;
    min-height: auto;
    gap: 4px;
    padding: 12px 8px;
    overflow-x: auto;
  }

  .app-sidebar {
    flex-direction: row;
    align-items: center;
  }

  .app-sidebar__nav {
    width: auto;
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  .app-sidebar__more {
    flex-direction: row;
    align-items: center;
  }

  .app-sidebar__brand {
    width: auto;
    padding: 0 12px;
  }

  .app-sidebar__item {
    width: auto;
    flex: 0 0 auto;
    padding: 8px 12px;
  }

  .app-sidebar__more {
    display: flex;
    flex: 0 0 auto;
    width: auto;
    margin-top: 0;
  }
}
</style>
